import React, { useState, useEffect } from 'react';
import { Tag, Clock, Percent, ArrowRight, Search } from 'lucide-react';
import ProductCard from '../common/ProductCard';
import { useToast } from '../../context/ToastContext';
import './SpecialOffersPage.css';
import * as ProductService from '../../services/ProductService';
import { DIRECT_PRODUCTS } from '../../data/ProductData';
import { CircularProgress, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const SpecialOffersPage = () => {
  const { showSuccessToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch offers and promotions
  useEffect(() => {
    const fetchSpecialOffers = async () => {
      setLoading(true);
      try {
        // Use direct products instead of API
        const specialOffers = DIRECT_PRODUCTS
          .filter(p => p.inStock && (p.discount > 0 || p.featured));
        
        // Add calculated discounted price for each product
        const offersWithDiscounts = specialOffers.map(product => ({
          ...product,
          originalPrice: product.price,
          discountedPrice: product.discount ? product.price * (1 - product.discount / 100) : product.price,
          endDate: '2023-12-31' // Default end date for all offers
        }));
        
        setProducts(offersWithDiscounts);
        setFilteredProducts(offersWithDiscounts);
      } catch (error) {
        console.error('Error fetching special offers:', error);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialOffers();
  }, []);

  // Filter products when category or search term changes
  useEffect(() => {
    let filtered = products;
    
    // First filter by search term if it exists
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(term) ||
        (product.description && product.description.toLowerCase().includes(term))
      );
    }
    
    // Then filter by category if not 'all'
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }
    
    setFilteredProducts(filtered);
  }, [selectedCategory, products, searchTerm]);

  // Format price with currency symbol
  const formatPrice = (price) => {
    if (price === undefined || price === null) {
      return '$0.00';
    }
    return `$${Number(price).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Calculate days remaining for an offer
  const getDaysRemaining = (endDate) => {
    if (!endDate) return 30; // Default to 30 days if no end date
    
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Handle newsletter subscription
  const handleSubscribe = (e) => {
    e.preventDefault();
    
    if (!email) {
      return;
    }
    
    setIsSubscribing(true);
    
    // Simulate API call
    setTimeout(() => {
      showSuccessToast('Thank you for subscribing to our newsletter!');
      setEmail('');
      setIsSubscribing(false);
    }, 1000);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Handle search change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Featured offers
  const featuredOffers = filteredProducts.filter(offer => offer.featured);
  // Regular offers
  const regularOffers = filteredProducts.filter(offer => !offer.featured);

  // Create structured data for special offers
  const specialOffersStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": filteredProducts.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "image": product.image.startsWith('http') 
          ? product.image 
          : `https://nmdiamond.in${product.image}`,
        "description": product.description || `${product.name} from NM Diamond`,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "INR",
          "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "offerCount": 1
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "reviewCount": "10"
        }
      }
    }))
  };

  return (
    <section className="collection-page special-offers-page">
      <Helmet>
        <title>Special Offers | NM Diamond</title>
        <meta name="description" content="Exclusive special offers on our premium diamond jewelry collection. Limited time discounts on rings, necklaces, earrings, and bracelets." />
        <script type="application/ld+json">
          {JSON.stringify(specialOffersStructuredData)}
        </script>
      </Helmet>
      
      <div className="special-offers-page">
        {/* Main Heading */}
        <div className="offers-header">
          <h1 className="offers-title">SPECIAL OFFERS</h1>
          <p className="section-subtitle">
            Exclusive deals on our premium diamond jewelry collection
          </p>
        </div>

        {loading ? (
          <div className="loading-container">
            <CircularProgress color="inherit" />
            <p>Loading special offers...</p>
          </div>
        ) : (
          <>
            {/* Search Bar */}
            <div className="search-container">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="search-input"
                />
                <Search size={20} className="search-icon" />
              </div>
            </div>

            {/* Category Filters */}
            <div className="category-filters">
              <button 
                className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('all')}
              >
                All
              </button>
              <button 
                className={`filter-btn ${selectedCategory === 'rings' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('rings')}
              >
                Rings
              </button>
              <button 
                className={`filter-btn ${selectedCategory === 'necklaces' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('necklaces')}
              >
                Necklaces
              </button>
              <button 
                className={`filter-btn ${selectedCategory === 'earrings' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('earrings')}
              >
                Earrings
              </button>
              <button 
                className={`filter-btn ${selectedCategory === 'bracelets' ? 'active' : ''}`}
                onClick={() => handleCategoryChange('bracelets')}
              >
                Bracelets
              </button>
            </div>

            {filteredProducts.length === 0 && (
              <div className="no-products-message">
                {searchTerm ? 
                  `No special offers found matching "${searchTerm}" in this category. Try a different search term or category.` :
                  'No special offers available in this category at the moment.'
                }
              </div>
            )}

            {/* Featured Offers */}
            {featuredOffers.length > 0 && (
              <section className="featured-offers-section">
                <div className="container">
                  <h2 className="offers-section-title">Featured Offers</h2>
                  <div className="product-grid">
                    {featuredOffers.map(offer => (
                      <ProductCard key={offer.id} product={offer} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Regular Offers */}
            {regularOffers.length > 0 && (
              <section className="regular-offers-section">
                <div className="container">
                  <h2 className="offers-section-title">More Offers</h2>
                  <div className="product-grid">
                    {regularOffers.map(offer => (
                      <ProductCard key={offer.id} product={offer} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Newsletter Subscription */}
            <section className="newsletter-section">
              <div className="container">
                <div className="newsletter-container">
                  <div className="newsletter-content">
                    <h2>Get Exclusive Offers</h2>
                    <p>
                      Subscribe to our newsletter and be the first to know about our special offers,
                      new arrivals, and exclusive promotions.
                    </p>
                    <form className="newsletter-form" onSubmit={handleSubscribe}>
                      <input
                        type="email"
                        placeholder="Your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                      <button type="submit" disabled={isSubscribing}>
                        {isSubscribing ? 'Subscribing...' : 'Subscribe'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </section>
  );
};

export default SpecialOffersPage; 